import { graphql, PageProps } from "gatsby";
import React, { FC, ReactNode, useContext } from "react";
import { GetArticleQuery, SanityArticle } from "../../types/graphql-types";
import { Card } from "../components/cards/card";
import { ExtendedPortableText } from "../components/ExtendedPortableText";
import { PageIntroProps } from "../components/PageIntro";
import { CardCollectionSection } from "../components/sections/CardCollectionSection";
import { VideoBlock } from "../components/VideoBlock";
import { PageContext } from "../context/PageContext";
import { useUrlMap } from "../hooks/useUrlMap";
import { useTranslations } from "../localization/Localization";
import urlFor from "../utilities/images/imageBuilder";
import { isNullOrWhiteSpace } from "../utilities/string";
import { filterRelatedArticles } from "../utilities/tag";
import { Article } from "./layouts/Article";
import { SanitySiteLayout } from "./layouts/SanitySiteLayout";

const SanityArticleTemplate: FC<PageProps<GetArticleQuery>> = ({ data }) => {
  const pageContext = useContext(PageContext);
  const urlMap = useUrlMap();
  const translations = useTranslations();

  if (!data.currentPage) {
    return null;
  }

  const { currentPage, relatedArticles } = data;

  const breadcrumbs = pageContext.parentNodes?.map(parentNode => ({
    url: urlMap[parentNode._id ?? ""] ?? "/",
    title: parentNode.title ?? "",
    label: parentNode.title ?? "",
    isLast: false
  }));

  breadcrumbs?.push({
    label: currentPage.title ?? "",
    title: currentPage.title ?? "",
    url: "",
    isLast: true
  });

  const tagNames = currentPage.tags?.map(
    x => x?.title && x.title[pageContext.language.replace("-", "_")]
  );

  const articleHeader: PageIntroProps = {
    heading: currentPage.title ?? "",
    preamble: currentPage.preamble ?? "",
    breadcrumbs: breadcrumbs ?? [],
    imageUrl:
      (currentPage.image?.asset &&
        urlFor(currentPage.image).height(1500).width(2000).url()) ??
      "",
    tags: tagNames
  };

  const filteredRelatedArticles = filterRelatedArticles(
    currentPage as SanityArticle,
    relatedArticles?.nodes as SanityArticle[]
  );

  let relatedCards!: Card[];

  if (filteredRelatedArticles) {
    relatedCards = filteredRelatedArticles.map(relatedArticle => ({
      date: "",
      heading: relatedArticle.title ?? "",
      body: relatedArticle.teaserText ?? "",
      link: {
        title: relatedArticle.title ?? "",
        url: urlMap[relatedArticle._id ?? ""],
        label: ""
      },
      imageUrl:
        (relatedArticle.image?.asset &&
          urlFor(relatedArticle.image).height(450).width(600).url()) ??
        "",
      tags: relatedArticle.tags?.map(
        x => x?.title && x.title[pageContext.language.replace("-", "_")]
      ),
      icon: "Chat"
    }));
  }

  if (currentPage.lastChangeDate) {
    articleHeader.lastChangeDate = currentPage.lastChangeDate;
  } else if (!isNullOrWhiteSpace(currentPage._updatedAt)) {
    articleHeader.lastChangeDate = new Date(currentPage._updatedAt);
  }

  articleHeader.language = pageContext.language;

  const blocks: ReactNode[] = [];

  if (relatedCards?.length > 0) {
    blocks.push(
      <CardCollectionSection
        body=""
        cardType="article"
        heading={translations.related.articles}
        subheading=""
        theme="default"
        cards={relatedCards}
      />
    );
  }

  return (
    <SanitySiteLayout>
      <Article
        articleHeader={articleHeader}
        blocks={blocks}
        videoBlock={<VideoBlock video={currentPage.videoBlocks?.[0]} />}
      >
        <ExtendedPortableText value={currentPage._rawMainBody} />
      </Article>
    </SanitySiteLayout>
  );
};

export { Head } from "../components/Head";

export const query = graphql`
  query GetArticle($_id: String!) {
    currentPage: sanityArticle(_id: { eq: $_id }) {
      title
      preamble
      _updatedAt
      language
      lastChangeDate
      _id
      slug {
        current
      }
      image {
        ...SanityExtendedImageFragment
      }
      _rawMainBody(resolveReferences: { maxDepth: 10 })
      tags {
        ...SanityTagFragment
      }
      videoBlocks {
        videoId
        videoText
      }
    }
    relatedArticles: allSanityArticle {
      nodes {
        title
        language
        slug {
          current
        }
        teaserText
        image {
          ...SanityExtendedImageFragment
        }
        _id
        tags {
          ...SanityTagFragment
        }
      }
    }
  }
`;

export default SanityArticleTemplate;
